import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { LucideMinimize2 } from '@lucide/angular';
import { DayBlock } from '../today/day.model';
import { computeActiveElapsedMs, computePlanMs, formatElapsedMs, formatOverrunWords } from './timer-elapsed.util';

/**
 * TimerOverlayComponent displays the focus timer panel for the currently running activity block.
 * It supports pause/resume/complete/skip actions and shows pause-aware elapsed time.
 *
 * @author sadatmalik
 */
@Component({
  selector: 'app-timer-overlay',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, LucideMinimize2],
  templateUrl: './timer-overlay.component.html',
  styleUrls: ['./timer-overlay.component.scss']
})
export class TimerOverlayComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input({ required: true }) runningBlock!: DayBlock;
  @Input({ required: true }) activityName = '';
  @Input() slotProgress: string | null = null;
  @Input() remainingSlots: number | null = null;
  @Input() submitting = false;
  @Input() error: string | null = null;
  /** When both are set, the card shows an "in <group>" link to the running group. */
  @Input() groupLinkLabel: string | null = null;
  @Input() groupLinkCommands: string[] | null = null;
  /** When set, the activity name links to the activity's page (its detail and tasks). */
  @Input() activityLinkCommands: string[] | null = null;
  @Input() activityLinkQueryParams: Record<string, string> | null = null;

  private readonly router = inject(Router);

  /** With an activity link, the whole card opens the activity (like the collapsed running row). */
  get opensActivity(): boolean {
    return !!this.activityLinkCommands?.length;
  }

  onCardClick(event: MouseEvent): void {
    if (!this.opensActivity) {
      return;
    }
    const target = event.target instanceof Element ? event.target : null;
    // Controls keep their own job; the name link already navigates by itself.
    if (target?.closest('button, a, input, select, textarea, label, [role="button"]')) {
      return;
    }
    // Don't hijack a text selection (e.g. copying the elapsed time).
    if (typeof window !== 'undefined' && window.getSelection()?.toString()) {
      return;
    }
    void this.router.navigate(this.activityLinkCommands ?? [], {
      queryParams: this.activityLinkQueryParams ?? {}
    });
  }

  @Output() complete = new EventEmitter<void>();
  @Output() skip = new EventEmitter<string>();
  @Output() pause = new EventEmitter<void>();
  @Output() resume = new EventEmitter<void>();
  @Output() refreshDayRequest = new EventEmitter<void>();
  @Output() hide = new EventEmitter<void>();
  @Output() autoAdvance = new EventEmitter<void>();

  readonly elapsedMs = signal(0);
  readonly milestoneAnnouncement = signal<string | null>(null);
  readonly autoAdvanceCountdownSec = signal<number | null>(null);
  skipReason = 'Skipped';

  @ViewChild('dialogContainer') dialogContainer?: ElementRef<HTMLElement>;

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private countdownIntervalId: ReturnType<typeof setInterval> | null = null;
  private previousActiveElapsedMs = 0;
  private eligibleForMilestone = false;
  private milestoneReached = false;
  private wasPaused = false;
  private lastBlockId: string | null = null;
  private hasPlayedMilestoneChime = false;

  private readonly milestoneChimePreferenceKey = 'lumen.focusTimerMilestoneChimeEnabled';

  ngOnInit(): void {
    this.startTicker();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('runningBlock' in changes) {
      this.startTicker();
    }
  }

  ngAfterViewInit(): void {
    // Move keyboard focus into the expanded timer panel when it opens.
    this.focusDialogContainer();
  }

  ngOnDestroy(): void {
    this.clearTicker();
    this.cancelAutoAdvanceCountdown();
  }

  onComplete(): void {
    if (this.submitting) return;
    this.complete.emit();
  }

  onSkip(): void {
    if (this.submitting) return;
    const reason = this.skipReason.trim();
    this.skip.emit(reason.length > 0 ? reason : 'Skipped');
  }

  onRefreshDay(): void {
    if (this.submitting) return;
    this.refreshDayRequest.emit();
  }

  onHide(): void {
    this.hide.emit();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    event.stopPropagation();
    this.onHide();
  }

  onPause(): void {
    if (this.submitting || this.isPaused()) return;
    this.cancelAutoAdvanceCountdown();
    this.pause.emit();
  }

  onResume(): void {
    if (this.submitting || !this.isPaused()) return;
    this.resume.emit();
  }

  isPaused(): boolean {
    return !!this.runningBlock?.pausedAt;
  }

  showPlannedCompleteBanner(): boolean {
    return this.milestoneReached && !this.isPaused();
  }

  isOverTime(): boolean {
    const planMs = this.planMs();
    return planMs > 0 && !this.isPaused() && this.elapsedMs() >= planMs;
  }

  readoutOverrunMs(): number {
    const planMs = this.planMs();
    if (planMs <= 0 || this.isPaused()) {
      return 0;
    }
    return Math.max(0, this.elapsedMs() - planMs);
  }

  overrunMs(): number {
    const planMs = this.planMs();
    if (planMs <= 0 || !this.showPlannedCompleteBanner()) {
      return 0;
    }
    return Math.max(0, this.elapsedMs() - planMs);
  }

  formatElapsed(value: number): string {
    return formatElapsedMs(value);
  }

  formatOverrun(value: number): string {
    return formatOverrunWords(value);
  }

  private startTicker(): void {
    this.clearTicker();
    this.updateElapsed();
    this.intervalId = setInterval(() => this.updateElapsed(), 1000);
  }

  private clearTicker(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private updateElapsed(): void {
    if (!this.runningBlock?.startedAt) {
      this.elapsedMs.set(0);
      this.resetMilestoneState();
      return;
    }
    const nowMs = Date.now();
    const currentActiveElapsedMs = computeActiveElapsedMs(
      {
        startedAt: this.runningBlock.startedAt,
        pausedAt: this.runningBlock.pausedAt,
        accumulatedPauseMs: this.runningBlock.accumulatedPauseMs
      },
      nowMs
    );
    this.elapsedMs.set(currentActiveElapsedMs);
    this.evaluateMilestoneState(currentActiveElapsedMs);
    this.previousActiveElapsedMs = currentActiveElapsedMs;
    this.wasPaused = this.isPaused();
  }

  private focusDialogContainer(): void {
    this.dialogContainer?.nativeElement.focus();
  }

  private planMs(): number {
    return computePlanMs(this.runningBlock?.durationMinutes);
  }

  private evaluateMilestoneState(currentActiveElapsedMs: number): void {
    const blockId = this.runningBlock?.id ?? null;
    if (blockId !== this.lastBlockId) {
      this.initializeMilestoneForCurrentBlock(currentActiveElapsedMs);
    }

    const planMs = this.planMs();
    if (planMs <= 0) {
      this.milestoneReached = false;
      this.eligibleForMilestone = false;
      this.milestoneAnnouncement.set(null);
      return;
    }

    const pausedNow = this.isPaused();
    const resumedThisTick = this.wasPaused && !pausedNow;
    if (resumedThisTick) {
      if (currentActiveElapsedMs >= planMs) {
        // Product rule: after resuming while already at/over plan, suppress milestone for remainder of block.
        this.milestoneReached = false;
        this.eligibleForMilestone = false;
        this.milestoneAnnouncement.set(null);
      } else {
        this.eligibleForMilestone = true;
      }
    }

    if (
      !pausedNow &&
      this.eligibleForMilestone &&
      !this.milestoneReached &&
      this.previousActiveElapsedMs < planMs &&
      currentActiveElapsedMs >= planMs
    ) {
      this.milestoneReached = true;
      this.eligibleForMilestone = false;
      this.milestoneAnnouncement.set('Planned focus time complete.');
      this.playMilestoneChimeIfEnabled();
      if ((this.remainingSlots ?? 0) > 0) {
        this.startAutoAdvanceCountdown();
      }
    }
  }

  private initializeMilestoneForCurrentBlock(currentActiveElapsedMs: number): void {
    this.lastBlockId = this.runningBlock?.id ?? null;
    this.milestoneReached = false;
    this.milestoneAnnouncement.set(null);
    this.hasPlayedMilestoneChime = false;
    this.wasPaused = this.isPaused();
    this.previousActiveElapsedMs = currentActiveElapsedMs;
    this.eligibleForMilestone = currentActiveElapsedMs < this.planMs();
    this.cancelAutoAdvanceCountdown();
  }

  private resetMilestoneState(): void {
    this.previousActiveElapsedMs = 0;
    this.eligibleForMilestone = false;
    this.milestoneReached = false;
    this.wasPaused = false;
    this.lastBlockId = null;
    this.hasPlayedMilestoneChime = false;
    this.milestoneAnnouncement.set(null);
    this.cancelAutoAdvanceCountdown();
  }

  cancelAutoAdvanceCountdown(): void {
    if (this.countdownIntervalId !== null) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
    this.autoAdvanceCountdownSec.set(null);
  }

  private startAutoAdvanceCountdown(seconds = 5): void {
    this.cancelAutoAdvanceCountdown();
    if (seconds <= 0) {
      this.autoAdvance.emit();
      return;
    }

    this.autoAdvanceCountdownSec.set(seconds);
    this.countdownIntervalId = setInterval(() => {
      const current = this.autoAdvanceCountdownSec();
      if (current === null) {
        this.cancelAutoAdvanceCountdown();
        return;
      }
      if (current <= 1) {
        this.cancelAutoAdvanceCountdown();
        this.autoAdvance.emit();
        return;
      }
      this.autoAdvanceCountdownSec.set(current - 1);
    }, 1000);
  }

  private playMilestoneChimeIfEnabled(): void {
    if (this.hasPlayedMilestoneChime || !this.isMilestoneChimeEnabled()) {
      return;
    }
    this.hasPlayedMilestoneChime = true;

    try {
      const globalWindow = window as Window & {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      };
      const AudioContextCtor = globalWindow.AudioContext ?? globalWindow.webkitAudioContext;
      if (!AudioContextCtor) {
        return;
      }
      const audioContext = new AudioContextCtor();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.08, audioContext.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, audioContext.currentTime + 0.35);

      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.36);
      oscillator.onended = () => {
        void audioContext.close();
      };
    } catch {
      // Ignore audio errors (autoplay policies, unsupported contexts, etc.).
    }
  }

  private isMilestoneChimeEnabled(): boolean {
    try {
      return localStorage.getItem(this.milestoneChimePreferenceKey) === 'true';
    } catch {
      return false;
    }
  }
}
